/**
 * KAN-835: менеджер сам подключает свой Telegram к боту для менеджеров.
 *
 * connect(): одноразовая ссылка t.me (POST /managers/me/telegram-link/),
 * открывается в Telegram; привязка случится, когда менеджер нажмёт "Start"
 * в боте, поэтому статус перечитывается при возврате в приложение.
 * disconnect(): DELETE /managers/me/telegram/.
 * Статус (telegram_linked, bot_connected) - из useMyNotificationsStatus.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { managerNotificationsApi } from '@/services/api/managerNotifications';
import { MY_NOTIFICATIONS_KEY } from '@/hooks/usePushSubscription';

/** Открыть ссылку Telegram: новое окно, если браузер его не дал - переход. */
export function openTelegramUrl(url: string, win: Window = window): void {
  // Без 'noopener' в features: с ним open() всегда возвращает null, и ссылка открылась бы дважды.
  const opened = win.open(url, '_blank');
  if (opened) opened.opener = null;
  else win.location.assign(url);
}

export function useMyTelegramLink() {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        void client.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
      }
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [client]);

  const connect = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const { url } = await managerNotificationsApi.telegramLink();
      openTelegramUrl(url);
    } catch (e) {
      setError(extractErrorMessage(e, 'Не удалось получить ссылку на бота'));
    } finally {
      setBusy(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      await managerNotificationsApi.telegramUnlink();
      await client.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
    } catch (e) {
      setError(extractErrorMessage(e, 'Не удалось отключить Telegram'));
    } finally {
      setBusy(false);
    }
  }, [client]);

  return { busy, error, connect, disconnect };
}
